// M004-F0769 / M004-F0770 / M004-F0771 (WebUI #246 phase 2): pure helpers for the advice statement panel. No React here.
// Behaviour source: design docs/design/M004-F0769-F0771-Advice-Statement-Panel.md sections 1 and 2. The server message is never shown:
// refusalMessage maps the classified outcome to the sentences in section 2.
import { classifyRelayOutcome } from '../../services/relayOutcome';
import { errorCode, errorDetails } from '../../services/billingProfileAdapter';

export const PILL_LABELS = Object.freeze({
  coming: 'Coming',
  being_checked: 'Being checked',
  deferred: 'Deferred',
  on_draft: 'On draft',
});

// Label for a line's pill. An unknown pill never shows its raw value. Owner ruling 2026-10-20: the advice note carries no
// invoiced lines, so 'invoiced' is not a pill (isAdviceNoteLine filters such lines out before they are rendered).
export function pillLabel(line) {
  const pill = line?.pill;
  if (!Object.prototype.hasOwnProperty.call(PILL_LABELS, pill)) return 'Unknown';
  return PILL_LABELS[pill];
}

// Owner ruling 2026-10-20: "Advice note should only include none invoice TXN". Lines the Ledger marks invoiced are never shown.
export function isAdviceNoteLine(line) {
  return line?.pill !== 'invoiced';
}

const londonParts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit' });

// 'YYYY-MM' of an instant as seen in Europe/London (BST/GMT aware).
export function londonMonthOf(dateStr) {
  const parts = londonParts.formatToParts(new Date(dateStr));
  const year = parts.find((p) => p.type === 'year').value;
  const month = parts.find((p) => p.type === 'month').value;
  return `${year}-${month}`;
}

const MONTH_NAMES = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
]);

// 'YYYY-MM' -> 'October 2026'.
export function monthHeading(month) {
  const [year, mm] = String(month).split('-');
  return `${MONTH_NAMES[Number(mm) - 1]} ${year}`;
}

// Lines grouped by London month of line.date, newest month first. Input order is kept within a month. subtotalMinor sums
// amount_minor of counted lines only (counted: false lines are shown muted and excluded from the total).
export function groupLinesByMonth(lines) {
  const byMonth = new Map();
  for (const line of Array.isArray(lines) ? lines.filter(isAdviceNoteLine) : []) {
    const month = londonMonthOf(line.date);
    if (!byMonth.has(month)) byMonth.set(month, []);
    byMonth.get(month).push(line);
  }
  return [...byMonth.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0)).map((month) => {
    const monthLines = byMonth.get(month);
    const subtotalMinor = monthLines
      .filter((line) => line.counted === true)
      .reduce((sum, line) => sum + line.amount_minor, 0);
    return { month, heading: monthHeading(month), lines: monthLines, subtotalMinor };
  });
}

// Totals rows in display order. VAT rows (Net, VAT, Gross) appear only when the account is VAT registered.
// Spec 1.26.0 TunnelAdviceNotePreviewPayload: vat_registered is a required top-level field; the amounts are in payload.totals.
export function totalsRows(payload) {
  const totals = payload?.totals || {};
  const vatRegistered = payload?.vat_registered === true;
  const rows = [];
  if (vatRegistered) {
    rows.push({ key: 'net', label: 'Net', minor: totals.net_minor });
    rows.push({ key: 'vat', label: 'VAT', minor: totals.vat_minor });
    rows.push({ key: 'gross', label: 'Gross', minor: totals.gross_minor });
  }
  rows.push({ key: 'credit', label: 'Credit', minor: totals.credit_minor });
  rows.push({ key: 'payable', label: 'Payable', minor: totals.payable_minor });
  return rows;
}

const MONTH_FORMAT = /^\d{4}-(0[1-9]|1[0-2])$/;

// Local refusal before any request. Returns null when the period is usable, else the sentence from section 2.
export function validatePeriod(from, to) {
  if (!MONTH_FORMAT.test(from) || !MONTH_FORMAT.test(to)) return 'Enter months as YYYY-MM.';
  if (to < from) return 'The To month must be the same as or after the From month.';
  return null;
}

const PERIOD_REASONS = Object.freeze({
  bad_format: 'Enter months as YYYY-MM.',
  to_before_from: 'The To month must be the same as or after the From month.',
  span_too_long: 'That period is too long. Choose a shorter period.',
  too_far_ahead: 'That period goes too far ahead. Choose an earlier To month.',
});
const UNKNOWN_MESSAGE = "We couldn't load the advice note right now. Try again.";
const COULD_NOT_SHOW = 'The advice note could not be shown.';

const REJECTED_MESSAGES = Object.freeze({
  LEDGER_ADVICE_NOTE_TOO_LARGE: 'This advice note is too large to download here. Choose a shorter period.',
  LEDGER_ACCOUNT_PERMISSION_DENIED: "You don't have permission to view this advice note.",
  LEDGER_DRAFT_NOT_UNIQUE: 'This customer has more than one open draft, so the advice note can\'t be produced. Ask your administrator to check the drafts.',
  LEDGER_ACCOUNT_OPERATION_UNSUPPORTED: "Advice notes aren't available here yet.",
});

// The sentence shown for a failed preview request. Core's 401 STEP_UP_REQUIRED opens the step-up dialog in the component and never reaches here;
// the node's own LEDGER_ACCOUNT_SAL_REQUIRED is a 403 refusal (the page rule, LedgerQueueSections) and gets its own sentence.
// Unknown outcomes are never worded as a failure. Any unmapped definite refusal reads as "could not be shown".
export const SAL_REQUIRED_MESSAGE = 'Viewing this advice note needs a fresh step-up sign-in. Sign in again, then try once more.';

export function refusalMessage(cause) {
  if (classifyRelayOutcome(cause) === 'unknown') return UNKNOWN_MESSAGE;
  const code = errorCode(cause);
  if (code === 'LEDGER_ACCOUNT_SAL_REQUIRED') return SAL_REQUIRED_MESSAGE;
  if (code === 'LEDGER_ADVICE_NOTE_PERIOD_INVALID') {
    const reason = errorDetails(cause).reason ?? cause?.response?.data?.error?.details?.reason;
    return Object.prototype.hasOwnProperty.call(PERIOD_REASONS, reason) ? PERIOD_REASONS[reason] : COULD_NOT_SHOW;
  }
  return Object.prototype.hasOwnProperty.call(REJECTED_MESSAGES, code) ? REJECTED_MESSAGES[code] : COULD_NOT_SHOW;
}

// Blob of payload.content_type from the base64 payload.pdf_base64.
export function pdfBlobFromPayload(payload) {
  const binary = atob(String(payload?.pdf_base64 || ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: payload?.content_type });
}
